import L from 'leaflet'
import { useEffect, useRef } from 'react'
import ReactDOMServer from 'react-dom/server'

import { Depot } from '@/entities/depots/models/Depot'
import { DepotMarkerIcon } from '@/shared/components/MapIcons/DepotMarkerIcon'

export type DrawDepotsArgs = {
  map: L.Map | null
  depots: Depot[]
}

/**
 * The following hook is used to draw the depots on the map widget.
 * 
 * @param map the Leaflet map object
 * @param depots the depots to plot on the map
 * @returns void
 */
export const useDrawDepots = ({ map, depots }: DrawDepotsArgs) => {
  const depotMarkersRef = useRef<L.LayerGroup | null>(null)

  const createDepotIcon = (depot: Depot) => {
    const svgString = ReactDOMServer.renderToStaticMarkup(
      <DepotMarkerIcon
        fillColor={depot?.uiColor || '#000'}
        className="depot-icon-svg"
      />,
    )

    return L.divIcon({
      html: svgString,
      className: '',
      iconSize: [30, 40],
      iconAnchor: [15, 40],
    })
  }

  // Plot depots on the map
  useEffect(() => {
    if (!map) return

    if (depotMarkersRef.current) {
      depotMarkersRef.current.clearLayers()
    } else {
      depotMarkersRef.current = L.layerGroup().addTo(map)
    }
    
    depots.forEach((depot) => {
      if (!depot.x || !depot.y) return
      
      const marker = L.marker([depot.y, depot.x], { icon: createDepotIcon(depot) })

      marker.bindTooltip(
        `<p style='margin: 2px 0'><strong>Depot Name:</strong> ${depot.name}</p><p style='margin: 2px 0'><strong>Address:</strong> ${depot.displayAddress}</p>`,
        {
          permanent: false,
          direction: 'right',
          offset: L.point(15, -25),
        },
      )

      depotMarkersRef.current!.addLayer(marker)
    })

    return () => {
      // Need to clear the markers when the component unmounts
      //   so it can be re-added to the map when the component mounts again
      if (depotMarkersRef.current) {
        depotMarkersRef.current.clearLayers()
        depotMarkersRef.current = null
      }
    }
  }, [map, depots])
}